import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Package,
  PackagePlus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  UserRoundPlus,
  Warehouse,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/PageSkeleton";
import { DatePicker } from "@/components/ui/DatePicker";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import i18n from "@/i18n";
import { formatLocalizedDate } from "@/lib/localized-date";
import {
  apiCreateEquipment,
  apiCreateEquipmentStock,
  apiAssignEquipmentStock,
  apiDeleteEquipment,
  apiDeleteEquipmentStock,
  apiGetEmployees,
  apiGetEquipment,
  apiGetEquipmentStock,
  apiReassignEquipment,
  apiUpdateEquipment,
  apiUpdateEquipmentStock,
  ApiError,
  type Equipment,
  type EquipmentPayload,
  type EquipmentStatus,
  type EquipmentStock,
  type EquipmentStockPayload,
} from "@/lib/api";

type TabKey = "overview" | "assigned" | "expiring" | "stock";

const EQUIPMENT_STATUSES: EquipmentStatus[] = ["Active", "Expired", "Returned", "Lost"];

const EQUIPMENT_API_ERROR_KEYS: Record<string, string> = {
  EQUIPMENT_ITEM_NAME_REQUIRED: "equipmentManagement.validation.itemNameRequired",
  EQUIPMENT_ASSIGNED_DATE_REQUIRED: "equipmentManagement.validation.assignedDateRequired",
  EQUIPMENT_INVALID_ASSIGNED_DATE: "equipmentManagement.validation.invalidAssignedDate",
  EQUIPMENT_INVALID_RETURN_DATE: "equipmentManagement.validation.invalidReturnDate",
  EQUIPMENT_INVALID_EXPIRY_DATE: "equipmentManagement.validation.invalidExpiryDate",
  EQUIPMENT_INVALID_EMPLOYEE: "equipmentManagement.validation.invalidEmployee",
  EQUIPMENT_INVALID_STATUS: "equipmentManagement.validation.invalidStatus",
  EQUIPMENT_STOCK_UNAVAILABLE: "equipmentManagement.assignStock.noAvailability",
};

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#14b8a6"];

const STATUS_COLORS: Record<EquipmentStatus, string> = {
  Active: "#10b981",
  Expired: "#ef4444",
  Returned: "#64748b",
  Lost: "#f59e0b",
};

const EMPTY_EQUIPMENT: EquipmentPayload = {
  employeeId: null,
  itemName: "",
  serialNumber: "",
  inventoryNumber: "",
  externalId: "",
  category: "",
  assignedDate: "",
  returnDate: "",
  expiryDate: "",
  status: "Active",
};

const EMPTY_STOCK: EquipmentStockPayload = {
  name: "",
  category: "",
  externalId: "",
  totalQuantity: 0,
  assignedQuantity: 0,
  notes: "",
};

function fmtDate(value?: string | null) {
  if (!value) return "-";
  return formatLocalizedDate(value, i18n.resolvedLanguage || i18n.language) || value;
}

function statusClass(status: EquipmentStatus) {
  if (status === "Active") return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
  if (status === "Expired") return "bg-red-500/10 text-red-700 border-red-200";
  if (status === "Returned") return "bg-slate-500/10 text-slate-700 border-slate-200";
  return "bg-amber-500/10 text-amber-700 border-amber-200";
}

function nullableDate(value?: string | null) {
  return value?.trim() ? value : null;
}

function normalizeEquipmentPayload(form: EquipmentPayload): EquipmentPayload {
  return {
    ...form,
    employeeId: form.employeeId === "" ? null : form.employeeId,
    assignedDate: nullableDate(form.assignedDate),
    returnDate: nullableDate(form.returnDate),
    expiryDate: nullableDate(form.expiryDate),
  };
}

function StatusBadge({ status }: { status: EquipmentStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={statusClass(status)}>
      {t(`equipmentManagement.status.${status}`)}
    </Badge>
  );
}

function StatCard({
  label,
  value,
  icon,
  onClick,
  active,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-center gap-3.5 px-5 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        active ? "bg-primary/5" : "hover:bg-muted/40"
      }`}
    >
      <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-primary/10 text-primary shrink-0">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
      </div>
    </button>
  );
}

export default function EquipmentManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isHrAdmin = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<EquipmentStatus | "all">("all");
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [equipmentForm, setEquipmentForm] = useState<EquipmentPayload>(EMPTY_EQUIPMENT);
  const [equipmentErrors, setEquipmentErrors] = useState<{ itemName?: string; assignedDate?: string }>({});
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [editingStock, setEditingStock] = useState<EquipmentStock | null>(null);
  const [stockForm, setStockForm] = useState<EquipmentStockPayload>(EMPTY_STOCK);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignItem, setReassignItem] = useState<Equipment | null>(null);
  const [reassignEmployeeId, setReassignEmployeeId] = useState("");
  const [assignStockOpen, setAssignStockOpen] = useState(false);
  const [assignStockItem, setAssignStockItem] = useState<EquipmentStock | null>(null);
  const [assignStockEmployeeId, setAssignStockEmployeeId] = useState("");
  const [assignStockDate, setAssignStockDate] = useState(new Date().toISOString().slice(0, 10));
  const [assignStockSerial, setAssignStockSerial] = useState("");
  const [assignStockInventory, setAssignStockInventory] = useState("");

  const equipmentQuery = useQuery({
    queryKey: ["equipment-management"],
    queryFn: () => apiGetEquipment(),
    enabled: isHrAdmin,
  });

  const stockQuery = useQuery({
    queryKey: ["equipment-stock"],
    queryFn: apiGetEquipmentStock,
    enabled: isHrAdmin,
  });

  const employeesQuery = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: isHrAdmin,
  });

  const equipment = useMemo(() => equipmentQuery.data ?? [], [equipmentQuery.data]);
  const stock = useMemo(() => stockQuery.data ?? [], [stockQuery.data]);
  const employees = employeesQuery.data ?? [];
  const selectedEmployeeEquipment = useMemo(() => {
    if (equipmentForm.employeeId == null || equipmentForm.employeeId === "") return [];
    const selectedEmployeeId = String(equipmentForm.employeeId);
    return equipment.filter((item) => String(item.employeeId) === selectedEmployeeId && item.status !== "Returned");
  }, [equipment, equipmentForm.employeeId]);

  const stats = useMemo(() => {
    return {
      active: equipment.filter((item) => item.status === "Active" && item.employeeId != null).length,
      expiring: equipment.filter((item) => item.status === "Active" && item.expiryDate && daysUntil(item.expiryDate) <= 30 && daysUntil(item.expiryDate) >= 0).length,
      stock: stock.reduce((sum, item) => sum + item.totalQuantity, 0),
    };
  }, [equipment, stock]);

  const overview = useMemo(() => {
    const unassigned = equipment.filter((item) => item.employeeId == null && item.status === "Active").length;

    const byStatus = [
      {
        key: "assigned",
        label: t("equipmentManagement.overview.assigned"),
        count: equipment.filter((item) => item.status === "Active" && item.employeeId != null).length,
        color: STATUS_COLORS.Active,
      },
      {
        key: "preallocated",
        label: t("equipmentManagement.overview.preallocated"),
        count: unassigned,
        color: "#3b82f6",
      },
      ...(["Expired", "Returned", "Lost"] as EquipmentStatus[]).map((status) => ({
        key: status,
        label: t(`equipmentManagement.status.${status}`),
        count: equipment.filter((item) => item.status === status).length,
        color: STATUS_COLORS[status],
      })),
    ]
      .filter((entry) => entry.count > 0);

    const byCategory = Array.from(
      equipment.reduce((m, item) => {
        const cat = item.category?.trim() || "Uncategorized";
        m.set(cat, (m.get(cat) ?? 0) + 1);
        return m;
      }, new Map<string, number>()),
    )
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const expiringItems = equipment
      .filter((item) => item.status === "Active" && item.expiryDate && daysUntil(item.expiryDate) <= 30)
      .sort((a, b) => daysUntil(a.expiryDate!) - daysUntil(b.expiryDate!));

    const stockTotal = stock.reduce((sum, item) => sum + item.totalQuantity, 0);
    const stockEmployeeAssigned = stock.reduce((sum, item) => sum + item.employeeAssignedQuantity, 0);
    const stockReserved = stock.reduce((sum, item) => sum + item.reservedQuantity, 0);
    const stockAvailable = stock.reduce((sum, item) => sum + item.availableQuantity, 0);

    return { unassigned, byStatus, byCategory, expiringItems, stockTotal, stockEmployeeAssigned, stockReserved, stockAvailable };
  }, [equipment, stock, t]);

  const filteredEquipment = useMemo(() => {
    const q = search.trim().toLowerCase();
    return equipment.filter((item) => {
      if (activeTab === "assigned" && item.employeeId == null) return false;
      if (activeTab === "expiring" && !(item.status === "Active" && item.expiryDate && daysUntil(item.expiryDate) <= 30 && daysUntil(item.expiryDate) >= 0)) return false;
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!q) return true;
      return [
        item.itemName,
        item.serialNumber,
        item.externalId,
        item.category,
        item.employeeName,
      ].some((value) => value?.toLowerCase().includes(q));
    });
  }, [activeTab, equipment, search, statusFilter]);

  const filteredStock = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stock;
    return stock.filter((item) =>
      [item.name, item.category, item.externalId, item.notes].some((value) =>
        value?.toLowerCase().includes(q),
      ),
    );
  }, [search, stock]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["equipment-management"] });
    queryClient.invalidateQueries({ queryKey: ["equipment-stock"] });
    queryClient.invalidateQueries({ queryKey: ["all-equipment-dash"] });
    queryClient.invalidateQueries({ queryKey: ["equipment-stock-dash"] });
    queryClient.invalidateQueries({ queryKey: ["all-equipment-analytics"] });
    queryClient.invalidateQueries({ queryKey: ["equipment-stock-analytics"] });
  };

  const equipmentMutation = useMutation({
    mutationFn: async () => {
      const payload = normalizeEquipmentPayload(equipmentForm);
      if (editingEquipment) {
        return apiUpdateEquipment(editingEquipment.id, payload);
      }
      return apiCreateEquipment(payload);
    },
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.saved"));
      setEquipmentDialogOpen(false);
      setEditingEquipment(null);
      setEquipmentForm(EMPTY_EQUIPMENT);
      setEquipmentErrors({});
      refreshAll();
    },
    onError: (error) => {
      const errorKey = error instanceof ApiError && error.code
        ? EQUIPMENT_API_ERROR_KEYS[error.code]
        : undefined;
      toast.error(t(errorKey ?? "equipmentManagement.toast.error"));
    },
  });

  const returnMutation = useMutation({
    mutationFn: (id: number) => apiDeleteEquipment(id),
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.returned"));
      refreshAll();
    },
    onError: (error) => {
      const errorKey = error instanceof ApiError && error.code
        ? EQUIPMENT_API_ERROR_KEYS[error.code]
        : undefined;
      toast.error(t(errorKey ?? "equipmentManagement.toast.error"));
    },
  });

  const reassignMutation = useMutation({
    mutationFn: () =>
      apiReassignEquipment(reassignItem!.id, {
        employeeId: reassignEmployeeId || null,
        assignedDate: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.reassigned"));
      setReassignOpen(false);
      setReassignItem(null);
      setReassignEmployeeId("");
      refreshAll();
    },
    onError: () => toast.error(t("equipmentManagement.toast.error")),
  });

  const stockMutation = useMutation({
    mutationFn: async () => {
      if (editingStock) return apiUpdateEquipmentStock(editingStock.id, stockForm);
      return apiCreateEquipmentStock(stockForm);
    },
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.stockSaved"));
      setStockDialogOpen(false);
      setEditingStock(null);
      setStockForm(EMPTY_STOCK);
      refreshAll();
    },
    onError: () => toast.error(t("equipmentManagement.toast.error")),
  });

  const deleteStockMutation = useMutation({
    mutationFn: (id: number) => apiDeleteEquipmentStock(id),
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.stockDeleted"));
      refreshAll();
    },
    onError: () => toast.error(t("equipmentManagement.toast.error")),
  });

  const assignStockMutation = useMutation({
    mutationFn: () =>
      apiAssignEquipmentStock(assignStockItem!.id, {
        employeeId: assignStockEmployeeId,
        serialNumber: assignStockSerial || null,
        inventoryNumber: assignStockInventory || null,
        assignedDate: assignStockDate,
      }),
    onSuccess: () => {
      toast.success(t("equipmentManagement.toast.stockAssigned"));
      setAssignStockOpen(false);
      setAssignStockItem(null);
      setAssignStockEmployeeId("");
      setAssignStockDate(new Date().toISOString().slice(0, 10));
      setAssignStockSerial("");
      setAssignStockInventory("");
      refreshAll();
    },
    onError: (error) => {
      const errorKey = error instanceof ApiError && error.code
        ? EQUIPMENT_API_ERROR_KEYS[error.code]
        : undefined;
      toast.error(t(errorKey ?? "equipmentManagement.toast.error"));
    },
  });

  function openCreateEquipment() {
    setEditingEquipment(null);
    setEquipmentForm(EMPTY_EQUIPMENT);
    setEquipmentErrors({});
    setEquipmentDialogOpen(true);
  }

  function openEditEquipment(item: Equipment) {
    setEditingEquipment(item);
    setEquipmentForm({
      employeeId: item.employeeId,
      itemName: item.itemName,
      serialNumber: item.serialNumber ?? "",
      inventoryNumber: item.inventoryNumber ?? "",
      externalId: item.externalId ?? "",
      category: item.category ?? "",
      assignedDate: item.assignedDate ?? "",
      returnDate: item.returnDate ?? "",
      expiryDate: item.expiryDate ?? "",
      status: item.status,
    });
    setEquipmentErrors({});
    setEquipmentDialogOpen(true);
  }

  function saveEquipment() {
    const errors = {
      itemName: equipmentForm.itemName.trim()
        ? undefined
        : t("equipmentManagement.validation.itemNameRequired"),
      assignedDate: equipmentForm.assignedDate?.trim()
        ? undefined
        : t("equipmentManagement.validation.assignedDateRequired"),
    };
    setEquipmentErrors(errors);
    const firstError = errors.itemName ?? errors.assignedDate;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    equipmentMutation.mutate();
  }

  function openCreateStock() {
    setEditingStock(null);
    setStockForm(EMPTY_STOCK);
    setStockDialogOpen(true);
  }

  function openEditStock(item: EquipmentStock) {
    setEditingStock(item);
    setStockForm({
      name: item.name,
      category: item.category ?? "",
      externalId: item.externalId ?? "",
      totalQuantity: item.totalQuantity,
      assignedQuantity: item.assignedQuantity,
      notes: item.notes ?? "",
    });
    setStockDialogOpen(true);
  }

  function openReassign(item: Equipment) {
    setReassignItem(item);
    setReassignEmployeeId(item.employeeId ?? "");
    setReassignOpen(true);
  }

  function openAssignStock(item: EquipmentStock) {
    setAssignStockItem(item);
    setAssignStockEmployeeId("");
    setAssignStockDate(new Date().toISOString().slice(0, 10));
    setAssignStockSerial("");
    setAssignStockInventory("");
    setAssignStockOpen(true);
  }

  function showEquipmentTab(tab: TabKey, status: EquipmentStatus | "all" = "all") {
    setActiveTab(tab);
    setStatusFilter(status);
  }

  const equipmentSaveDisabled = equipmentMutation.isPending;

  if (!isHrAdmin) {
    return (
      <AppLayout>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {t("equipmentManagement.accessDenied")}
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between animate-in fade-in slide-in-from-left-5 duration-500">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("equipmentManagement.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("equipmentManagement.subtitle")}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refreshAll()} disabled={equipmentQuery.isFetching || stockQuery.isFetching}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("common.refresh")}
            </Button>
            <Button onClick={activeTab === "stock" ? openCreateStock : openCreateEquipment}>
              {activeTab === "stock" ? <PackagePlus className="h-4 w-4 mr-2" /> : <UserRoundPlus className="h-4 w-4 mr-2" />}
              {activeTab === "stock" ? t("equipmentManagement.addStock") : t("equipmentManagement.addEquipment")}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-3 divide-x divide-border/60">
          <StatCard
            label={t("equipmentManagement.stats.active")}
            value={stats.active}
            icon={<CheckCircle2 className="h-4 w-4" />}
            onClick={() => showEquipmentTab("assigned", "Active")}
            active={activeTab === "assigned" && statusFilter === "Active"}
          />
          <StatCard
            label={t("equipmentManagement.stats.expiring")}
            value={stats.expiring}
            icon={<CalendarClock className="h-4 w-4" />}
            onClick={() => showEquipmentTab("expiring")}
            active={activeTab === "expiring"}
          />
          <StatCard
            label={t("equipmentManagement.stats.stock")}
            value={stats.stock}
            icon={<Warehouse className="h-4 w-4" />}
            onClick={() => showEquipmentTab("stock")}
            active={activeTab === "stock"}
          />
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabKey)}>
                <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
                  <TabsTrigger value="overview" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">{t("equipmentManagement.tabs.overview")}</TabsTrigger>
                  <TabsTrigger value="assigned" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">{t("equipmentManagement.tabs.assigned")}</TabsTrigger>
                  <TabsTrigger value="expiring" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">{t("equipmentManagement.tabs.expiring")}</TabsTrigger>
                  <TabsTrigger value="stock" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                    {t("equipmentManagement.tabs.stock")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {activeTab !== "overview" && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative sm:w-72">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t("equipmentManagement.search")}
                    />
                  </div>
                  {activeTab !== "stock" && (
                    <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as EquipmentStatus | "all")}>
                      <SelectTrigger className="sm:w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("equipmentManagement.status.all")}</SelectItem>
                        {EQUIPMENT_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`equipmentManagement.status.${status}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {activeTab === "overview" ? (
              <EquipmentOverview
                equipment={equipment}
                overview={overview}
                isLoading={equipmentQuery.isLoading || stockQuery.isLoading}
              />
            ) : activeTab === "stock" ? (
              stockQuery.isLoading ? (
                <TableSkeleton rows={6} cols={6} />
              ) : (
                <StockTable
                  stock={filteredStock}
                  onCreate={openCreateStock}
                  onEdit={openEditStock}
                  onDelete={(id) => deleteStockMutation.mutate(id)}
                  onAssign={openAssignStock}
                />
              )
            ) : equipmentQuery.isLoading ? (
              <TableSkeleton rows={6} cols={6} />
            ) : (
              <EquipmentTable
                equipment={filteredEquipment}
                onEdit={openEditEquipment}
                onReturn={(id) => returnMutation.mutate(id)}
                onReassign={openReassign}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEquipment ? t("equipmentManagement.dialog.editEquipment") : t("equipmentManagement.dialog.addEquipment")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("equipmentManagement.fields.employee")} className="sm:col-span-2">
              <EmployeeCombobox
                employees={employees}
                value={equipmentForm.employeeId == null ? "" : String(equipmentForm.employeeId)}
                onChange={(value) => setEquipmentForm((prev) => ({ ...prev, employeeId: value }))}
                placeholder={t("equipmentManagement.fields.unassigned")}
                className="h-10 rounded-md min-w-0"
              />
              {equipmentForm.employeeId != null && equipmentForm.employeeId !== "" && (
                <AssignedEquipmentSummary items={selectedEmployeeEquipment} />
              )}
            </Field>
            <Field label={t("equipmentManagement.fields.status")}>
              <Select value={equipmentForm.status ?? "Active"} onValueChange={(value) => setEquipmentForm((prev) => ({ ...prev, status: value as EquipmentStatus }))}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {t(`equipmentManagement.status.${status}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("equipmentManagement.fields.itemName")} required error={equipmentErrors.itemName}>
              <Input
                value={equipmentForm.itemName}
                aria-invalid={!!equipmentErrors.itemName}
                onChange={(event) => {
                  setEquipmentForm((prev) => ({ ...prev, itemName: event.target.value }));
                  if (equipmentErrors.itemName) setEquipmentErrors((prev) => ({ ...prev, itemName: undefined }));
                }}
              />
            </Field>
            <Field label={t("equipmentManagement.fields.serialNumber")}>
              <Input value={equipmentForm.serialNumber ?? ""} onChange={(event) => setEquipmentForm((prev) => ({ ...prev, serialNumber: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.inventoryNumber")}>
              <Input value={equipmentForm.inventoryNumber ?? ""} onChange={(event) => setEquipmentForm((prev) => ({ ...prev, inventoryNumber: event.target.value }))} />
            </Field>
            {editingEquipment?.besthrNumber && (
              <Field label={t("equipmentManagement.fields.besthrNumber")}>
                <div className="flex h-10 items-center rounded-md border border-border/60 bg-muted/40 px-3 text-sm font-mono text-muted-foreground select-all">
                  {editingEquipment.besthrNumber}
                </div>
              </Field>
            )}
            <Field label={t("equipmentManagement.fields.externalId")}>
              <Input value={equipmentForm.externalId ?? ""} onChange={(event) => setEquipmentForm((prev) => ({ ...prev, externalId: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.category")}>
              <Input value={equipmentForm.category ?? ""} onChange={(event) => setEquipmentForm((prev) => ({ ...prev, category: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.assignedDate")} required error={equipmentErrors.assignedDate}>
              <DatePicker
                value={equipmentForm.assignedDate ?? ""}
                onChange={(value) => {
                  setEquipmentForm((prev) => ({ ...prev, assignedDate: value }));
                  if (equipmentErrors.assignedDate) setEquipmentErrors((prev) => ({ ...prev, assignedDate: undefined }));
                }}
              />
            </Field>
            <Field label={t("equipmentManagement.fields.returnDate")}>
              <DatePicker value={equipmentForm.returnDate ?? ""} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, returnDate: value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.expiryDate")}>
              <DatePicker value={equipmentForm.expiryDate ?? ""} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, expiryDate: value }))} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipmentDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveEquipment} disabled={equipmentSaveDisabled}>
              {equipmentMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingStock ? t("equipmentManagement.dialog.editStock") : t("equipmentManagement.dialog.addStock")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("equipmentManagement.fields.name")}>
              <Input value={stockForm.name} onChange={(event) => setStockForm((prev) => ({ ...prev, name: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.category")}>
              <Input value={stockForm.category ?? ""} onChange={(event) => setStockForm((prev) => ({ ...prev, category: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.externalId")}>
              <Input value={stockForm.externalId ?? ""} onChange={(event) => setStockForm((prev) => ({ ...prev, externalId: event.target.value }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.totalQuantity")}>
              <Input type="number" min={0} value={stockForm.totalQuantity ?? 0} onChange={(event) => setStockForm((prev) => ({ ...prev, totalQuantity: Number(event.target.value) }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.assignedQuantity")}>
              <Input type="number" min={0} value={stockForm.assignedQuantity ?? 0} onChange={(event) => setStockForm((prev) => ({ ...prev, assignedQuantity: Number(event.target.value) }))} />
            </Field>
            <Field label={t("equipmentManagement.fields.notes")} className="sm:col-span-2">
              <Textarea value={stockForm.notes ?? ""} onChange={(event) => setStockForm((prev) => ({ ...prev, notes: event.target.value }))} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => stockMutation.mutate()} disabled={!stockForm.name.trim() || stockMutation.isPending}>
              {stockMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignStockOpen} onOpenChange={setAssignStockOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("equipmentManagement.dialog.assignStock")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">{assignStockItem?.name}</p>
              <p className="text-xs text-muted-foreground">
                {assignStockItem?.category ?? ""}
                {assignStockItem?.externalId ? ` · ${assignStockItem.externalId}` : ""}
              </p>
              {assignStockItem && (
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  <p>
                    {t("equipmentManagement.fields.availableQuantity")}: {" "}
                    <span className={assignStockItem.availableQuantity === 0 ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>
                      {assignStockItem.availableQuantity}
                    </span>
                    {" / "}{assignStockItem.totalQuantity}
                  </p>
                  {assignStockItem.reservedQuantity > 0 && (
                    <p className="font-medium text-blue-600">
                      {t("equipmentManagement.assignStock.preallocatedAvailable", { count: assignStockItem.reservedQuantity })}
                    </p>
                  )}
                </div>
              )}
            </div>
            {assignStockItem && assignStockItem.availableQuantity === 0 && assignStockItem.reservedQuantity === 0 ? (
              <p className="text-sm text-amber-600">{t("equipmentManagement.assignStock.noAvailability")}</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("equipmentManagement.fields.employee")} className="sm:col-span-2">
                  <EmployeeCombobox
                    employees={employees}
                    value={assignStockEmployeeId}
                    onChange={setAssignStockEmployeeId}
                    placeholder={t("equipmentManagement.fields.unassigned")}
                    className="h-10 rounded-md"
                  />
                </Field>
                <Field label={t("equipmentManagement.fields.serialNumber")}>
                  <Input value={assignStockSerial} onChange={(e) => setAssignStockSerial(e.target.value)} placeholder={t("equipmentManagement.fields.serialNumberPlaceholder")} />
                </Field>
                <Field label={t("equipmentManagement.fields.inventoryNumber")}>
                  <Input value={assignStockInventory} onChange={(e) => setAssignStockInventory(e.target.value)} placeholder={t("equipmentManagement.fields.inventoryNumberPlaceholder")} />
                </Field>
                <Field label={t("equipmentManagement.fields.assignedDate")} required>
                  <DatePicker value={assignStockDate} onChange={setAssignStockDate} />
                </Field>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignStockOpen(false)}>{t("common.cancel")}</Button>
            {assignStockItem && (assignStockItem.availableQuantity > 0 || assignStockItem.reservedQuantity > 0) && (
              <Button
                onClick={() => assignStockMutation.mutate()}
                disabled={!assignStockEmployeeId || !assignStockDate || assignStockMutation.isPending}
              >
                {assignStockMutation.isPending ? t("common.saving") : t("equipmentManagement.actions.assignToEmployee")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("equipmentManagement.dialog.reassign")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">{reassignItem?.itemName}</p>
              <p className="text-xs text-muted-foreground">{reassignItem?.serialNumber || reassignItem?.externalId || "-"}</p>
            </div>
            <Field label={t("equipmentManagement.fields.employee")}>
              <div className="flex gap-2">
                <EmployeeCombobox
                  employees={employees}
                  value={reassignEmployeeId}
                  onChange={setReassignEmployeeId}
                  placeholder={t("equipmentManagement.fields.unassigned")}
                  className="h-10 rounded-md"
                />
                <Button type="button" variant="outline" onClick={() => setReassignEmployeeId("")}>
                  {t("common.none")}
                </Button>
              </div>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => reassignMutation.mutate()} disabled={!reassignItem || reassignMutation.isPending}>
              {reassignMutation.isPending ? t("common.saving") : t("equipmentManagement.actions.reassign")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function EquipmentOverview({
  equipment,
  overview,
  isLoading,
}: {
  equipment: Equipment[];
  overview: {
    unassigned: number;
    byStatus: { key: string; label: string; count: number; color: string }[];
    byCategory: { category: string; count: number }[];
    expiringItems: Equipment[];
    stockTotal: number;
    stockEmployeeAssigned: number;
    stockReserved: number;
    stockAvailable: number;
  };
  isLoading?: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
      </div>
    );
  }

  const assigned = equipment.filter((item) => item.employeeId != null && item.status === "Active").length;
  const expiringCount = overview.expiringItems.length;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t("equipmentManagement.overview.totalItems"), value: equipment.length, color: "" },
          { label: t("equipmentManagement.overview.assigned"), value: assigned, color: "" },
          { label: t("equipmentManagement.overview.preallocated"), value: overview.unassigned, color: overview.unassigned > 0 ? "text-amber-600" : "" },
          { label: t("equipmentManagement.overview.expiringSoon"), value: expiringCount, color: expiringCount > 0 ? "text-amber-600" : "" },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg bg-muted/50 p-4">
            <p className={`text-2xl font-bold tabular-nums leading-none ${color}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{t("equipmentManagement.overview.byStatus")}</CardTitle>
          </CardHeader>
          <CardContent>
            {overview.byStatus.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("equipmentManagement.overview.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={overview.byStatus}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {overview.byStatus.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 13 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By category */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{t("equipmentManagement.overview.byCategory")}</CardTitle>
          </CardHeader>
          <CardContent>
            {overview.byCategory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("equipmentManagement.overview.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={overview.byCategory} margin={{ top: 5, right: 20, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="category" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 13 }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} name={t("equipmentManagement.overview.items")}>
                    {overview.byCategory.map((_entry, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock overview */}
      <Card>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Warehouse className="h-4 w-4 text-slate-600" />
            {t("equipmentManagement.overview.stockOverview")}
          </CardTitle>
          <CardDescription>{t("equipmentManagement.overview.stockOverviewDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: t("equipmentManagement.fields.totalQuantity"), value: overview.stockTotal },
              { label: t("equipmentManagement.fields.employeeAssignedQuantity"), value: overview.stockEmployeeAssigned },
              { label: t("equipmentManagement.fields.preallocatedQuantity"), value: overview.stockReserved },
              { label: t("equipmentManagement.fields.availableQuantity"), value: overview.stockAvailable },
            ].map(({ label, value }) => (
              <div key={label} className="text-center rounded-md bg-muted/40 py-3 px-1">
                <p className="text-xl font-bold tabular-nums">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Expiring soon */}
      {overview.expiringItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              {t("equipmentManagement.overview.expiringTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">{t("equipmentManagement.fields.itemName")}</TableHead>
                    <TableHead className="text-xs">{t("equipmentManagement.fields.employee")}</TableHead>
                    <TableHead className="text-xs">{t("equipmentManagement.fields.category")}</TableHead>
                    <TableHead className="text-xs text-right">{t("equipmentManagement.fields.expiryDate")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.expiringItems.slice(0, 10).map((item) => {
                    const days = daysUntil(item.expiryDate!);
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="text-xs font-medium">{item.itemName}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.employeeName ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.category ?? "—"}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${days < 0 ? "text-red-600" : "text-amber-600"}`}>
                          {fmtDate(item.expiryDate)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {equipment.length === 0 && (
        <div className="py-8 text-center">
          <Package className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t("equipmentManagement.empty")}</p><p className="text-sm text-muted-foreground mt-2">{t("pilot.empty.equipment")}</p>
        </div>
      )}
    </div>
  );
}

function daysUntil(value: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = parseISO(value);
  date.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / 86_400_000);
}

function Field({
  label,
  className,
  children,
  required,
  error,
}: {
  label: string;
  className?: string;
  children: ReactNode;
  required?: boolean;
  error?: string;
}) {
  return (
    <label className={`block min-w-0 ${className ?? ""}`}>
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>}
      </span>
      <div className="mt-1 min-w-0">{children}</div>
      {error && <p className="mt-1 text-xs text-destructive" role="alert">{error}</p>}
    </label>
  );
}

function AssignedEquipmentSummary({ items }: { items: Equipment[] }) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-dashed border-border/80 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {t("equipmentManagement.employeeEquipment.none")}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-border/70 bg-muted/30 p-3">
      <p className="text-xs font-medium text-foreground">{t("equipmentManagement.employeeEquipment.title")}</p>
      <div className="mt-2 space-y-2">
        {items.map((item) => (
          <div key={item.id} className="rounded-md bg-background/80 px-3 py-2 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{item.itemName}</span>
              <StatusBadge status={item.status} />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {item.category && <span>{item.category}</span>}
              {item.assignedDate && <span>{t("equipmentManagement.fields.assignedDate")}: {fmtDate(item.assignedDate)}</span>}
              {item.expiryDate && <span>{t("equipmentManagement.fields.expiryDate")}: {fmtDate(item.expiryDate)}</span>}
              {item.returnDate && <span>{t("equipmentManagement.fields.returnDate")}: {fmtDate(item.returnDate)}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EquipmentTable({
  equipment,
  onEdit,
  onReturn,
  onReassign,
}: {
  equipment: Equipment[];
  onEdit: (item: Equipment) => void;
  onReturn: (id: number) => void;
  onReassign: (item: Equipment) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.itemName")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.employee")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.category")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.externalId")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.expiryDate")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("common.status")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right">{t("common.actions")}</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {equipment.map((item) => (
            <TableRow
              key={item.id}
              className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
              onClick={() => onEdit(item)}
            >
              <TableCell className="pl-4 py-3">
                <div className="font-medium">{item.itemName}</div>
                <div className="text-xs text-muted-foreground">{item.serialNumber || "-"}</div>
              </TableCell>
              <TableCell className="py-3">{item.employeeName ?? t("equipmentManagement.fields.unassigned")}</TableCell>
              <TableCell className="py-3">{item.category ?? "-"}</TableCell>
              <TableCell className="py-3">{item.externalId ?? "-"}</TableCell>
              <TableCell className="py-3">{fmtDate(item.expiryDate)}</TableCell>
              <TableCell className="py-3"><StatusBadge status={item.status} /></TableCell>
              <TableCell className="py-3">
                <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => onReassign(item)} title={t("equipmentManagement.actions.reassign")}>
                    <UserRoundPlus className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onReturn(item.id)} title={t("equipmentManagement.actions.return")}>
                    <RotateCcw className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </TableCell>
              <TableCell className="py-3 w-8 pr-3">
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
              </TableCell>
            </TableRow>
          ))}
          {equipment.length === 0 && (
            <TableRow>
              <TableCell className="py-8 text-center text-muted-foreground" colSpan={8}>
                {t("equipmentManagement.empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function StockTable({
  stock,
  onEdit,
  onDelete,
  onAssign,
  onCreate,
}: {
  stock: EquipmentStock[];
  onCreate?: () => void;
  onEdit: (item: EquipmentStock) => void;
  onDelete: (id: number) => void;
  onAssign: (item: EquipmentStock) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.name")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.category")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.externalId")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.totalQuantity")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.assignedQuantity")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("equipmentManagement.fields.availableQuantity")}</TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right">{t("common.actions")}</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {stock.map((item) => (
            <TableRow
              key={item.id}
              className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
              onClick={() => onEdit(item)}
            >
              <TableCell className="pl-4 py-3">
                <div className="font-medium">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.notes ?? "-"}</div>
              </TableCell>
              <TableCell className="py-3">{item.category ?? "-"}</TableCell>
              <TableCell className="py-3">{item.externalId ?? "-"}</TableCell>
              <TableCell className="py-3">{item.totalQuantity}</TableCell>
              <TableCell className="py-3">
                <div className="tabular-nums">{item.assignedQuantity}</div>
                <div className="text-xs text-muted-foreground">
                  {t("equipmentManagement.stockBreakdown.employeeAssigned", { count: item.employeeAssignedQuantity })} / {t("equipmentManagement.stockBreakdown.preallocated", { count: item.reservedQuantity })}
                </div>
              </TableCell>
              <TableCell className={`py-3 tabular-nums font-medium ${item.availableQuantity === 0 ? "text-amber-600" : "text-emerald-600"}`}>
                {item.availableQuantity}
              </TableCell>
              <TableCell className="py-3">
                <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAssign(item)}
                    title={t(
                      item.reservedQuantity > 0
                        ? "equipmentManagement.actions.assignPreallocatedUnit"
                        : "equipmentManagement.actions.assignToEmployee",
                    )}
                    disabled={item.availableQuantity === 0 && item.reservedQuantity === 0}
                    className="gap-1.5 whitespace-nowrap"
                  >
                    <UserRoundPlus className={`h-4 w-4 ${item.availableQuantity === 0 && item.reservedQuantity === 0 ? "text-muted-foreground/40" : ""}`} />
                    {t(
                      item.reservedQuantity > 0
                        ? "equipmentManagement.actions.assignPreallocatedUnit"
                        : "equipmentManagement.actions.assignToEmployee",
                    )}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} title={t("common.delete")}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
              <TableCell className="py-3 w-8 pr-3">
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
              </TableCell>
            </TableRow>
          ))}
          {stock.length === 0 && (
            <TableRow>
              <TableCell className="py-8 text-center text-muted-foreground" colSpan={8}>
                {t("equipmentManagement.emptyStock")}{onCreate && <Button className="mt-3 mx-auto block" onClick={onCreate}>{t("equipmentManagement.addStock")}</Button>}<p className="mt-2 text-sm">{t("pilot.empty.equipment")}</p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
